const FALLBACK_REFRESH_DELAY_MS = 6 * 60 * 1000;
const REFRESH_FRACTION = 0.75;
const MIN_REFRESH_DELAY_MS = 5 * 1000;
const MAX_REFRESH_DELAY_MS = 6 * 60 * 1000;
const EXPIRY_GUARD_MS = 1000;
const MAX_RETRY_DELAY_MS = 2 * 60 * 1000;
const EXPIRED_TOKEN_RETRY_BASE_DELAY_MS = 5 * 1000;
const MAX_REFRESH_FAILURES = 3;

type ExpiringSpeechToken = { expires_in?: number };

type SpeechTokenRefreshSchedulerOptions<T extends ExpiringSpeechToken> = {
  refreshToken: () => Promise<T>;
  isActive: () => boolean;
  onToken: (token: T) => void;
  onExpiryFallback: () => void;
  onRefreshFailure: (error: unknown) => void;
  onRetriesExhausted: () => void;
};

export function getSpeechTokenRefreshDelay(
  expiresInSeconds: number | null | undefined,
  receivedAtMs = Date.now(),
  nowMs = Date.now(),
  expiredLifetimeRetryAttempt = 1,
): { delayMs: number; usedFallback: boolean } {
  if (expiresInSeconds == null) {
    return { delayMs: FALLBACK_REFRESH_DELAY_MS, usedFallback: true };
  }
  const lifetimeMs = Number(expiresInSeconds) * 1000;

  if (!Number.isFinite(lifetimeMs)) {
    return { delayMs: FALLBACK_REFRESH_DELAY_MS, usedFallback: true };
  }
  if (lifetimeMs <= 0) {
    return {
      delayMs: getSpeechTokenExpiredLifetimeRetryDelay(
        expiredLifetimeRetryAttempt,
      ),
      usedFallback: false,
    };
  }

  // `expires_in` is relative to the response construction time. We start the
  // client clock at receipt and deduct any work that happened before scheduling;
  // no client/server clock comparison is involved.
  const delayFromReceiptMs = Math.min(
    Math.max(Math.round(lifetimeMs * REFRESH_FRACTION), MIN_REFRESH_DELAY_MS),
    MAX_REFRESH_DELAY_MS,
  );
  const elapsedSinceReceiptMs = Math.max(0, nowMs - receivedAtMs);
  const remainingLifetimeMs = lifetimeMs - elapsedSinceReceiptMs;

  return {
    // The five-second floor avoids immediate retries when the API reports a
    // very short remaining lifetime. The backend renews cached tokens before
    // they reach this point; the expiry guard applies above that floor.
    delayMs: Math.max(
      MIN_REFRESH_DELAY_MS,
      Math.min(
        delayFromReceiptMs - elapsedSinceReceiptMs,
        remainingLifetimeMs - EXPIRY_GUARD_MS,
      ),
    ),
    usedFallback: false,
  };
}

export function getSpeechTokenExpiredLifetimeRetryDelay(
  attempt: number,
): number {
  return Math.min(
    EXPIRED_TOKEN_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1),
    MAX_RETRY_DELAY_MS,
  );
}

export function getSpeechTokenRetryDelay(attempt: number): number {
  return Math.min(30 * 1000 * 2 ** (attempt - 1), MAX_RETRY_DELAY_MS);
}

export class SpeechTokenRefreshScheduler<T extends ExpiringSpeechToken> {
  private readonly options: SpeechTokenRefreshSchedulerOptions<T>;
  private timer: number | null = null;
  private refreshFailures = 0;
  private expiredLifetimeRetries = 0;
  private stopped = false;

  constructor(options: SpeechTokenRefreshSchedulerOptions<T>) {
    this.options = options;
  }

  start(
    expiresInSeconds: number | null | undefined,
    receivedAtMs = Date.now(),
  ): void {
    this.stopped = false;
    this.refreshFailures = 0;
    this.expiredLifetimeRetries = 0;
    this.scheduleForLifetime(expiresInSeconds, receivedAtMs);
  }

  stop(): void {
    this.stopped = true;
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private scheduleForLifetime(
    expiresInSeconds: number | null | undefined,
    receivedAtMs = Date.now(),
  ): void {
    const hasFiniteLifetime =
      expiresInSeconds != null && Number.isFinite(Number(expiresInSeconds));
    const isAlreadyExpired = hasFiniteLifetime && Number(expiresInSeconds) <= 0;
    if (isAlreadyExpired) {
      this.expiredLifetimeRetries += 1;
    } else {
      this.expiredLifetimeRetries = 0;
    }
    const { delayMs, usedFallback } = getSpeechTokenRefreshDelay(
      expiresInSeconds,
      receivedAtMs,
      Date.now(),
      this.expiredLifetimeRetries || 1,
    );
    if (usedFallback) this.options.onExpiryFallback();
    this.schedule(this.refresh, delayMs);
  }

  private schedule(callback: () => void, delayMs: number): void {
    if (this.stopped || !this.options.isActive()) return;
    this.timer = window.setTimeout(callback, delayMs);
  }

  private refresh = (): void => {
    this.timer = null;
    void this.refreshToken();
  };

  private async refreshToken(): Promise<void> {
    try {
      const token = await this.options.refreshToken();
      if (this.stopped || !this.options.isActive()) return;
      this.refreshFailures = 0;
      this.options.onToken(token);
      this.scheduleForLifetime(token.expires_in);
    } catch (error) {
      if (this.stopped || !this.options.isActive()) return;
      this.refreshFailures += 1;
      this.options.onRefreshFailure(error);
      if (this.refreshFailures >= MAX_REFRESH_FAILURES) {
        this.options.onRetriesExhausted();
        return;
      }
      this.schedule(
        this.refresh,
        getSpeechTokenRetryDelay(this.refreshFailures),
      );
    }
  }
}
